import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X as XIcon, Sparkles, Crown, Zap, Wallet } from "lucide-react";

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useSession } from "@/contexts/SessionContext";

const PLANS = [
    {
        id: "trial",
        label: "Trial",
        price: "Free",
        priceNote: "15 days",
        nxt: "50 NXT one-time",
        Icon: Sparkles,
        accent: "#8A93A6",
        features: [
            { ok: true, text: "Agent 1 (titles + captions)" },
            { ok: false, text: "Agent 2 — Audience Predict" },
            { ok: false, text: "Adsum QC Review" },
            { ok: false, text: "Top-ups" },
            { ok: true, text: "Personal brand only" },
        ],
    },
    {
        id: "creator",
        label: "Creator",
        price: "$24",
        priceNote: "/mo simulated",
        nxt: "+200 NXT every month",
        Icon: Zap,
        accent: "#2F58CD",
        features: [
            { ok: true, text: "Agent 1 (titles + captions)" },
            { ok: false, text: "Agent 2 — Audience Predict" },
            { ok: false, text: "Adsum QC Review" },
            { ok: true, text: "Top-ups ($5 → +500 NXT)" },
            { ok: true, text: "Personal brand only" },
        ],
    },
    {
        id: "pro",
        label: "Pro",
        price: "$79",
        priceNote: "/mo simulated",
        nxt: "+800 NXT every month",
        Icon: Crown,
        accent: "#00F5D4",
        features: [
            { ok: true, text: "Agent 1 (titles + captions)" },
            { ok: true, text: "Agent 2 — Audience Predict" },
            { ok: true, text: "Adsum QC Review (EN + ES)" },
            { ok: true, text: "Top-ups ($5 → +500 NXT)" },
            { ok: true, text: "Multi-brand (coming soon)" },
        ],
    },
];

export default function SubscriptionDrawer() {
    const {
        subscriptionDrawerOpen,
        setSubscriptionDrawerOpen,
        subscriptionReason,
        tier,
        subscribe,
        trialActive,
    } = useSession();
    const [busyTier, setBusyTier] = useState(null);
    const [flash, setFlash] = useState(false);

    const handleSubscribe = async (planId) => {
        if (planId === "trial") return;
        setBusyTier(planId);
        try {
            await subscribe(planId);
            setFlash(true);
            setTimeout(() => setFlash(false), 600);
            setTimeout(() => setSubscriptionDrawerOpen(false), 1100);
        } catch (e) {
            /* toast handled inside context */
        } finally {
            setBusyTier(null);
        }
    };

    return (
        <Sheet
            open={subscriptionDrawerOpen}
            onOpenChange={setSubscriptionDrawerOpen}
        >
            <SheetContent
                side="right"
                className="w-full overflow-y-auto border-l border-[#1F2A40] bg-[#0B0F19] p-0 text-[#E8ECF5] sm:max-w-[520px]"
                data-testid="subscription-drawer"
            >
                <AnimatePresence>
                    {flash && (
                        <motion.div
                            key="flash"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.45 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="pointer-events-none absolute inset-0 z-20"
                            style={{
                                background:
                                    "radial-gradient(circle at 50% 30%, rgba(0,245,212,0.5) 0%, transparent 70%)",
                            }}
                        />
                    )}
                </AnimatePresence>

                <SheetHeader className="sticky top-0 z-10 flex flex-col gap-2 border-b border-[#1F2A40] bg-[#0B0F19]/95 px-6 py-4 backdrop-blur">
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                            subscription · simulated
                        </span>
                        <button
                            type="button"
                            onClick={() => setSubscriptionDrawerOpen(false)}
                            className="rounded-md p-1.5 text-[#8A93A6] transition-colors hover:bg-[#111726] hover:text-[#E8ECF5]"
                            aria-label="Close"
                            data-testid="subscription-drawer-close"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <SheetTitle className="text-xl font-medium tracking-tight text-[#E8ECF5]">
                        Choose your plan
                    </SheetTitle>
                    <SheetDescription className="text-sm text-[#8A93A6]">
                        Tiered access to SmartPublish&apos;s premium agents.
                        Billing is simulated for this phase.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex flex-col gap-3 px-6 py-6">
                    {subscriptionReason?.code === "trial_expired" && (
                        <div
                            className="rounded-xl border border-[#FF5470]/40 bg-[#FF5470]/10 px-4 py-3 text-sm text-[#FF5470]"
                            data-testid="trial-expired-banner"
                        >
                            Your 15-day trial has ended. Choose a plan to keep
                            running agents.
                        </div>
                    )}
                    {subscriptionReason?.code === "tier_required" && (
                        <div
                            className="rounded-xl border border-[#00F5D4]/30 bg-[#00F5D4]/10 px-4 py-3 text-sm text-[#00F5D4]"
                            data-testid="tier-required-banner"
                        >
                            That feature needs{" "}
                            <span className="font-semibold capitalize">
                                {subscriptionReason.required || "a paid plan"}
                            </span>
                            . Pick a plan to unlock it.
                        </div>
                    )}

                    {PLANS.map((plan) => {
                        const isCurrent = plan.id === tier;
                        const cta = ctaLabel(plan.id, tier);
                        const disabled =
                            isCurrent ||
                            busyTier === plan.id ||
                            plan.id === "trial";
                        return (
                            <article
                                key={plan.id}
                                className={[
                                    "relative flex flex-col gap-3 rounded-2xl border bg-[#111726] p-5 transition-colors",
                                    isCurrent
                                        ? "border-[#00F5D4]/60"
                                        : "border-[#1F2A40] hover:border-[#1F2A40]/70",
                                ].join(" ")}
                                data-testid={`plan-card-${plan.id}`}
                                data-current={isCurrent ? "true" : "false"}
                            >
                                {isCurrent && (
                                    <span
                                        className="absolute right-4 top-4 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#00F5D4]"
                                        data-testid={`plan-current-pill-${plan.id}`}
                                    >
                                        current plan
                                    </span>
                                )}
                                <div className="flex items-center gap-3">
                                    <div
                                        className="flex h-9 w-9 items-center justify-center rounded-xl border bg-[#0B0F19]"
                                        style={{
                                            borderColor: plan.accent + "55",
                                            color: plan.accent,
                                        }}
                                    >
                                        <plan.Icon className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-medium text-[#E8ECF5]">
                                            {plan.label}
                                        </h3>
                                        <div className="flex items-baseline gap-1.5">
                                            <span
                                                className="text-2xl font-semibold tabular-nums"
                                                style={{ color: plan.accent }}
                                            >
                                                {plan.price}
                                            </span>
                                            <span className="font-mono text-[10px] text-[#8A93A6]">
                                                {plan.priceNote}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs font-medium text-[#E8ECF5]">
                                    <Wallet className="h-3 w-3 text-[#00F5D4]" />
                                    {plan.nxt}
                                </div>
                                <ul className="flex flex-col gap-1.5">
                                    {plan.features.map((f, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center gap-2 text-sm"
                                        >
                                            {f.ok ? (
                                                <Check
                                                    className="h-3.5 w-3.5 shrink-0 text-[#00F5D4]"
                                                    strokeWidth={3}
                                                />
                                            ) : (
                                                <XIcon
                                                    className="h-3.5 w-3.5 shrink-0 text-[#8A93A6]/50"
                                                    strokeWidth={3}
                                                />
                                            )}
                                            <span
                                                className={
                                                    f.ok
                                                        ? "text-[#E8ECF5]"
                                                        : "text-[#8A93A6]"
                                                }
                                            >
                                                {f.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={disabled}
                                    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-2.5 text-sm font-medium text-white transition-all hover:shadow-[0_0_24px_rgba(0,245,212,0.4)] disabled:cursor-not-allowed disabled:bg-[#1A2235] disabled:opacity-50 disabled:hover:shadow-none"
                                    data-testid={`plan-subscribe-${plan.id}`}
                                >
                                    {busyTier === plan.id
                                        ? "Processing…"
                                        : cta}
                                </button>
                            </article>
                        );
                    })}

                    <p
                        className="mt-2 text-center font-mono text-[10px] italic text-[#8A93A6]"
                        data-testid="billing-disclaimer"
                    >
                        Simulated billing — no real charges. Real Stripe
                        integration in a future phase.
                    </p>
                    {trialActive === false && tier === "trial" && (
                        <p className="text-center font-mono text-[10px] text-[#FF5470]">
                            Your trial has expired — pick a plan to continue.
                        </p>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function ctaLabel(planId, currentTier) {
    if (planId === "trial") return "Trial active";
    if (planId === currentTier) return "Current plan";
    if (currentTier === "trial") {
        return planId === "creator" ? "Subscribe to Creator" : "Subscribe to Pro";
    }
    if (currentTier === "creator" && planId === "pro") return "Upgrade to Pro";
    if (currentTier === "pro" && planId === "creator")
        return "Downgrade to Creator";
    return planId === "creator" ? "Switch to Creator" : "Switch to Pro";
}
